import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import useDebounce from '../../../hooks/useDebounce';
import useStopScrolling from '../../../hooks/useStopScrolling';
import AdminUserSideBar from '../../../components/AdminUserSideBar';

import PendingInvitesTable from './PendingInvitesTable';
import UnconfirmedUsersTable from './UnconfirmedUsersTable';
import ActiveUsersTable from './ActiveUsersTable';
import TabSelection from './TabSelection';
import { useQuery } from 'react-query';
import { adminAPI } from '../../../api';
import NonUsersTable from './NonUsersTable';

const COOKIE_SEARCH_QUERY = 'admin_user_search_query';
const COOKIE_FILTER = 'admin_user_filter';

const AdminUser = () => {
  // Load initial values from cookies
  const getInitialSearchQuery = () => {
    const saved = Cookies.get(COOKIE_SEARCH_QUERY);
    return saved || '';
  };

  const getInitialFilter = () => {
    const saved = Cookies.get(COOKIE_FILTER);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  };

  const [currentTab, setCurrentTab] = useState('Users and Invites');
  const [searchQuery, setSearchQuery] = useState(getInitialSearchQuery);
  const [checkedActiveUsers, setCheckedActiveUsers] = useState([]);
  const [unconfirmedUsers, setUnconfirmedUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [openSideActionBoard, setOpenSideActionBoard] = useState(); // {type: "", action: "", ids: # }
  const [filter, setFilter] = useState(getInitialFilter);
  const [activeUsersPage, setActiveUsersPage] = useState(1);
  const [unconfirmedUsersPage, setUnconfirmedUsersPage] = useState(1);
  const [pendingInvitesPage, setPendingInvitesPage] = useState(1);
  const [checkedAllActiveUsers, setCheckedAllActiveUsers] = useState(false);
  const [checkedAllUnconfirmedUsers, setCheckedAllUnconfirmedUsers] = useState(false);
  const [checkedAllPendingInvites, setCheckedAllPendingInvites] = useState(false);

  const handleSelectAllActiveUsers = (selectAll = false) => {
    if (selectAll) {
      setCheckedAllActiveUsers(true);
    } else {
      setCheckedAllActiveUsers(false);
      setCheckedActiveUsers([]);
    }
  };

  const handleSelectAllUnconfirmedUsers = (selectAll = false) => {
    if (selectAll) {
      setCheckedAllUnconfirmedUsers(true);
    } else {
      setCheckedAllUnconfirmedUsers(false);
      setUnconfirmedUsers([]);
    }
  };

  const handleSelectAllPendingInvites = (selectAll = false) => {
    if (selectAll) {
      setCheckedAllPendingInvites(true);
    } else {
      setCheckedAllPendingInvites(false);
      setPendingInvites([]);
    }
  };

  // Save searchQuery to cookie whenever it changes
  useEffect(() => {
    if (searchQuery) {
      Cookies.set(COOKIE_SEARCH_QUERY, searchQuery, { expires: 7 }); // expires in 7 days
    } else {
      Cookies.remove(COOKIE_SEARCH_QUERY);
    }
  }, [searchQuery]);

  // Save filter to cookie whenever it changes
  useEffect(() => {
    if (filter && Object.keys(filter).length > 0) {
      Cookies.set(COOKIE_FILTER, JSON.stringify(filter), { expires: 7 }); // expires in 7 days
    } else {
      Cookies.remove(COOKIE_FILTER);
    }
  }, [filter]);

  const handleSearchQueryChange = (newSearchQuery) => {
    setSearchQuery(newSearchQuery);

    setCheckedAllActiveUsers(false);
    setCheckedAllUnconfirmedUsers(false);
    setCheckedAllPendingInvites(false);
    setCheckedActiveUsers([]);
    setUnconfirmedUsers([]);
    setPendingInvites([]);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    Cookies.remove(COOKIE_SEARCH_QUERY);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleClearFilters = () => {
    setFilter({});
    Cookies.remove(COOKIE_FILTER);
  };

  const debouncedSearch = useDebounce(searchQuery, 300);
  const debouncedFilter = useDebounce(filter, 300);

  const { data } = useQuery(
    [
      'allUsers',
      {
        active_users_page: activeUsersPage,
        unconfirmed_users_page: unconfirmedUsersPage,
        pending_invites_page: pendingInvitesPage,
        search: debouncedSearch,
        filter: debouncedFilter,
        count: 50,
      },
    ],
    adminAPI.getUsers
  );

  const { data: invitationData } = useQuery(
    'invitationData',
    adminAPI.getInvitationData
  );

  useStopScrolling(openSideActionBoard);

  const hasNonUsers = data?.non_users?.length > 0;

  // Determine totalUsers based on the type of openSideActionBoard
  const getTotalUsersForSideBar = () => {
    if (!openSideActionBoard) return 0;
    
    const { type } = openSideActionBoard;
    switch (type) {
      case 'Active Users':
        return data?.confirmed?.pagination?.total_count || 0;
      case 'Unconfirmed Users':
        return data?.unconfirmed?.pagination?.total_count || 0;
      case 'Pending Invites':
        return data?.pending_invites?.pagination?.total_count || 0;
      case 'Invite Users':
        return 0; // For new invites, no existing users
      default:
        return 0;
    }
  };

  const totalUsersForSideBar = getTotalUsersForSideBar();

  return (
    <div>
      {openSideActionBoard && (
        <AdminUserSideBar
          openSideActionBoard={openSideActionBoard}
          setOpenSideActionBoard={setOpenSideActionBoard}
          setCheckedActiveUsers={setCheckedActiveUsers}
          setUnconfirmedUsers={setUnconfirmedUsers}
          setPendingInvites={setPendingInvites}
          checkedAllActiveUsers={checkedAllActiveUsers}
          setCheckedAllActiveUsers={setCheckedAllActiveUsers}
          checkedAllUnconfirmedUsers={checkedAllUnconfirmedUsers}
          setCheckedAllUnconfirmedUsers={setCheckedAllUnconfirmedUsers} 
          checkedAllPendingInvites={checkedAllPendingInvites}
          setCheckedAllPendingInvites={setCheckedAllPendingInvites}
          totalUsers={totalUsersForSideBar}
          cohorts={invitationData?.cohort_options || []}
          filter={filter}
          searchQuery={searchQuery}
        />
      )}
      <TabSelection
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        searchQuery={searchQuery}
        setSearchQuery={handleSearchQueryChange}
        onClearSearch={handleClearSearch}
        filter={filter}
        setFilter={handleFilterChange}
        onClearFilters={handleClearFilters}
        setOpenSideActionBoard={setOpenSideActionBoard}
        hasNonUsers={hasNonUsers}
        handleSelectAllActiveUsers={handleSelectAllActiveUsers}
        checkedActiveUsers={checkedActiveUsers}
        checkedAllActiveUsers={checkedAllActiveUsers}
        totalUsers={data?.confirmed?.pagination?.total_count || 0}
      />
      <div style={{ gap: '64px', overflow: 'auto' }} className="flex flex-col">
        {(currentTab === 'Active Users' ||
          currentTab === 'Users and Invites' ||
          searchQuery !== '') && (
          <ActiveUsersTable
            searchQuery={debouncedSearch}
            filter={filter}
            items={data?.confirmed}
            onPaginationChange={(page) => setActiveUsersPage(page)}
            checkedActiveUsers={checkedActiveUsers}
            setCheckedActiveUsers={setCheckedActiveUsers}
            setOpenSideActionBoard={setOpenSideActionBoard}
            checkedAllUsers={checkedAllActiveUsers}
            handleSelectAllUsers={handleSelectAllActiveUsers}
            totalUsers={data?.confirmed?.pagination?.total_count || 0}
          />
        )}
        {(currentTab === 'Unconfirmed Users' ||
          currentTab === 'Users and Invites' ||
          searchQuery !== '') && (
          <UnconfirmedUsersTable
            searchQuery={debouncedSearch}
            filter={filter}
            items={data?.unconfirmed}
            onPaginationChange={(page) => setUnconfirmedUsersPage(page)}
            unconfirmedUsers={unconfirmedUsers}
            setUnconfirmedUsers={setUnconfirmedUsers}
            setOpenSideActionBoard={setOpenSideActionBoard}
            checkedAllUsers={checkedAllUnconfirmedUsers}
            handleSelectAllUsers={handleSelectAllUnconfirmedUsers}
            totalUsers={data?.unconfirmed?.pagination?.total_count || 0}
          />
        )}
        {(currentTab === 'Pending Invites' ||
          currentTab === 'Users and Invites' ||
          searchQuery !== '') && (
          <PendingInvitesTable
            searchQuery={debouncedSearch}
            filter={filter}
            items={data?.pending_invites}
            onPaginationChange={(page) => setPendingInvitesPage(page)}
            pendingInvites={pendingInvites}
            setPendingInvites={setPendingInvites}
            setOpenSideActionBoard={setOpenSideActionBoard}
            checkedAllUsers={checkedAllPendingInvites}
            handleSelectAllUsers={handleSelectAllPendingInvites}
            totalUsers={data?.pending_invites?.pagination?.total_count || 0}
          />
        )}
        {hasNonUsers && (
          <NonUsersTable items={data?.non_users.map((email) => ({ email }))} />
        )}
      </div>
    </div>
  );
};

export default AdminUser;
